import { Link } from 'react-router-dom';
import type { BuildingType, BuildingZone, Planet, PlayerRace, TickCheckpointEventOf } from '@kaeliux/shared';
import { BUILDING_DEFINITIONS } from '@kaeliux/shared';
import { Button } from '../ui/Button';
import { ProgressBar } from '../ui/ProgressBar';
import UnmetRequirementsGrid, { unmetRequirementTile } from '../UnmetRequirementsGrid';
import QueueMaxButton from '../QueueMaxButton';
import UnlockSlotsButton from '../UnlockSlotsButton';
import { usePlanetBuildActions } from '../../hooks/usePlanetBuildActions';
import { useToast } from '../../contexts/ToastContext';
import { usePlanetView } from '../../contexts/PlanetViewContext';
import { getBuildingIcon } from '../../utils/buildingIcons';
import { formatCountdown } from '../../utils/formatters';
import type { PlanetActionOptions } from '../../services/planet.service';
import { ZONE_LABEL } from './zoneStyles';
import type { SwatchResourceKey } from './resourceSwatches';
import { CostChips } from './CostChips';

const SPRITE_SIZE = { width: 46, height: 61 };

export interface BuiltPlotContentProps {
    buildingType: BuildingType;
    zone: BuildingZone;
    /**
     * The tapped plot's id (DS-28) — only sent to the server, and only
     * meaningful, for a level 0→1 construct.
     */
    plotId: string;
    planet: Planet;
    race: PlayerRace | undefined;
    scienceLevels: Partial<Record<string, number>>;
    projectedLevel: number;
    queuedEvents: TickCheckpointEventOf<'buildingBuild'>[];
    queueFull: boolean;
    options: PlanetActionOptions;
    now: number;
    onClose: () => void;
}

/**
 * `BuildingPopover`'s content for a built (or under-construction) slot.
 * Mutations go entirely through `usePlanetBuildActions`, which both this
 * component and `ZonePage`'s `BuildingRow` consume — see that hook's doc
 * comment.
 */
export function BuiltPlotContent({ buildingType, zone, plotId, planet, race, scienceLevels, projectedLevel, queuedEvents, queueFull, options, now, onClose }: BuiltPlotContentProps) {
    const { show } = useToast();
    const view = usePlanetView();
    const def = BUILDING_DEFINITIONS[buildingType];
    const spriteRace = view.buildingRace(buildingType) ?? race;

    const {
        level,
        nextCost,
        atMax,
        unmetRequirements,
        affordable,
        disabled,
        disabledReason,
        isRequesting,
        error,
        handleUpgrade,
        queueMax,
        isQueueingMax,
        handleQueueMax,
        showUnlockSlots,
        cancelTarget,
        isCancelling,
        requestCancel,
        destroyRefund,
        isDestroying,
        destroyError,
        requestDestroy,
    } = usePlanetBuildActions({
        planet,
        buildingType,
        scienceLevels,
        projectedLevel,
        queuedEvents,
        queueFull,
        options,
        plotId,
        onUpgraded: () => {
            show(projectedLevel === 0 ? 'Construction queued' : 'Upgrade queued');
            onClose();
        },
        onQueuedMax: (count) => {
            show(`Queued ${count} levels`);
            onClose();
        },
        onCancelled: () => {
            show(level === 0 ? 'Construction cancelled' : 'Upgrade cancelled');
            onClose();
        },
        onDestroyed: () => {
            show('Destroy scheduled');
            onClose();
        },
    });

    const activeUpgrade = queuedEvents.find((event) => event.eventDetails.startDate.getTime() <= now);
    const isUpgrading = !!activeUpgrade;
    const remainingMs = activeUpgrade ? activeUpgrade.endDate.getTime() - now : 0;
    const progress = activeUpgrade
        ? 1 - remainingMs / (activeUpgrade.eventDetails.durationSeconds * 1_000)
        : 0;

    const primaryLabel = projectedLevel === 0 ? 'Construct' : 'Upgrade';
    const screenRoute = view.screenRoute(buildingType);
    const hasScreen = level > 0 && !!screenRoute;

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-start gap-2">
                <img
                    src={getBuildingIcon(buildingType, level, spriteRace)}
                    alt=""
                    width={SPRITE_SIZE.width}
                    height={SPRITE_SIZE.height}
                    className="rounded-tile border border-[color:var(--zone-accent)] bg-space-950 object-cover"
                />
                <div className="min-w-0">
                    <div className="text-title text-content-primary">{def.name}</div>
                    <div className="text-label text-content-muted">Level {level} · {ZONE_LABEL[zone]} Zone</div>
                </div>
            </div>

            <p className="text-body text-content-muted">{def.description}</p>

            {!atMax && (
                <CostChips
                    cost={nextCost}
                    testIdPrefix="building-popover-cost"
                    shortfallKeys={(Object.keys(nextCost) as SwatchResourceKey[]).filter((key) => {
                        if (!nextCost[key]) return false;
                        return key === 'energy'
                            ? disabledReason === 'Insufficient energy'
                            : !affordable && planet.resources[key].current < (nextCost[key] ?? 0);
                    })}
                />
            )}

            {unmetRequirements.length > 0 && (
                <UnmetRequirementsGrid tiles={unmetRequirements.map((req) => unmetRequirementTile(req, race))} />
            )}

            {error && <p className="text-body text-status-danger">{error}</p>}
            {destroyError && <p className="text-body text-status-danger">{destroyError}</p>}

            {hasScreen && (
                <Link to={screenRoute} onClick={onClose}>
                    <Button variant="secondary" fullWidth data-testid="building-popover-open-screen">
                        Open {def.name}
                    </Button>
                </Link>
            )}

            {isUpgrading && (
                <ProgressBar value={progress} label={`Upgrading… ${formatCountdown(remainingMs)}`} />
            )}

            {!view.canManage ? (
                <p className="text-label text-content-muted" data-testid="building-popover-read-only">{view.readOnlyReason}</p>
            ) : (
                <>
                    <div className="flex gap-2">
                        <Button
                            variant="primary"
                            fullWidth
                            disabled={disabled}
                            loading={isRequesting}
                            title={disabled && disabledReason ? disabledReason : undefined}
                            onClick={() => { handleUpgrade().catch(() => { }); }}
                        >
                            {atMax ? 'Max level' : primaryLabel}
                        </Button>
                        {level >= 1 && view.allowDestroy && (
                            <Button
                                variant="danger"
                                loading={isDestroying}
                                disabled={isRequesting || isQueueingMax || isCancelling || !destroyRefund}
                                onClick={requestDestroy}
                            >
                                Destroy
                            </Button>
                        )}
                    </div>
                    {cancelTarget && (
                        <Button
                            variant="secondary"
                            size="sm"
                            fullWidth
                            loading={isCancelling}
                            disabled={isRequesting || isQueueingMax || isDestroying}
                            onClick={() => requestCancel(now)}
                            data-testid="building-popover-cancel"
                        >
                            {cancelTarget.eventDetails.targetLevel === 1 ? 'Cancel construction' : `Cancel level ${cancelTarget.eventDetails.targetLevel}`}
                        </Button>
                    )}
                    <QueueMaxButton
                        preview={queueMax}
                        loading={isQueueingMax}
                        disabled={isRequesting || isQueueingMax || isDestroying || isCancelling}
                        onQueue={() => { handleQueueMax().catch(() => { }); }}
                        fullWidth
                    />
                    {showUnlockSlots && <UnlockSlotsButton />}
                </>
            )}

            <Link
                to={view.zoneRoute(zone, def.id)}
                onClick={onClose}
                className="text-label text-content-muted underline"
            >
                View in list →
            </Link>
        </div>
    );
}

export default BuiltPlotContent;
